import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, Empty, Button } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { ArrowRight, Calendar, CreditCard } from "lucide-react";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function statusVariant(s) {
  if (s === "completed") return "success";
  if (s === "in_progress") return "primary";
  if (s === "review") return "warning";
  return "default";
}

export default function CustomerProjects() {
  const [projects, setProjects] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/projects");
      setProjects(data || []);
    })();
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="My workspace · Projects"
        title="Projects"
        subtitle="Everything we're building for you, with live status and payment progress."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {projects.map((p) => {
          const f = p.financials || {};
          const isFixed = (f.billing_type || "fixed") === "fixed" && (f.budget || 0) > 0;
          const paid = f.paid || 0;
          const denom = isFixed ? (f.budget || 0) : (f.invoiced || 0);
          const paymentPct = denom > 0 ? Math.round((paid / denom) * 100) : 0;
          return (
            <div
              key={p.id}
              onClick={() => nav(`/my-projects/${p.id}`)}
              className="group rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden cursor-pointer hover:-translate-y-[2px] hover:shadow-[0_16px_40px_-18px_hsl(var(--primary)/0.4)] transition-all"
              data-testid="customer-project-card"
            >
              <div className="relative h-36 w-full overflow-hidden">
                {p.cover_image_url
                  ? <img src={p.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  : <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))]" />}
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--surface))]/95 to-transparent" />
                <div className="absolute top-3 right-3">
                  <StatusPill variant={statusVariant(p.status)}>{p.status.replace("_", " ")}</StatusPill>
                </div>
                <div className="absolute bottom-3 left-4 right-4">
                  <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">{p.project_type || "Project"}</div>
                  <div className="text-[18px] font-bold leading-tight mt-0.5">{p.name}</div>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">{isFixed ? "Paid / Budget" : "Paid / Invoiced"}</div>
                    <div className="font-mono text-[15px] font-semibold mt-1">{gbp(paid)} / {gbp(denom)}</div>
                  </div>
                  <div className="rounded-lg bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">Development</div>
                    <div className="font-mono text-[15px] font-semibold mt-1">{p.progress || 0}%</div>
                  </div>
                </div>
                {isFixed ? (
                  <div>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-1">
                      <span>Payment progress</span>
                      <span className="font-mono">{paymentPct}%</span>
                    </div>
                    <div className="h-1.5 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[hsl(var(--success))] to-[hsl(var(--primary))]" style={{ width: `${Math.min(paymentPct, 100)}%` }} />
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">
                    {f.outstanding > 0 ? <span className="text-amber-600 dark:text-amber-400 font-semibold">{gbp(f.outstanding)} outstanding</span> : "All paid. Thank you."}
                  </div>
                )}
                <button className="w-full h-10 rounded-lg bg-[hsl(var(--primary))] text-white font-semibold text-[13px] flex items-center justify-center gap-2 hover:bg-[hsl(var(--primary-hover))] transition-colors">
                  View project <ArrowRight className="w-3.5 h-3.5" strokeWidth={2}/>
                </button>
              </div>
            </div>
          );
        })}
        {projects.length === 0 && <div className="md:col-span-2 xl:col-span-3"><Empty title="No projects yet" subtitle="New projects will appear here as they're set up."/></div>}
      </div>
    </div>
  );
}
